<template>
  <div ref="container" style="min-height: 65px;"></div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'

const props = defineProps({
  invisible: { type: Boolean, default: false },
  theme: { type: String, default: 'light' }
})

const emit = defineEmits(['verified', 'error', 'expired'])

const container = ref(null)
let widgetId = null
let renderAttempts = 0

const isLocalDevelopment = computed(() => {
  const hostname = window.location.hostname
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local')
})

const TEST_SITE_KEY = '1x00000000000000000000AA'

const effectiveSiteKey = computed(() =>
  isLocalDevelopment.value
    ? TEST_SITE_KEY
    : import.meta.env.VITE_CAPTCHA_CLOUDFLARE_SITE_KEY,
)

function loadScript() {
  return new Promise((resolve, reject) => {
    if (window.turnstile?.render) return resolve()

    const existingScript = document.querySelector('script[src*="turnstile"]')
    if (existingScript) {
      if (window.turnstile?.render) resolve()
      else existingScript.addEventListener('load', () => setTimeout(resolve, 200))
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => setTimeout(resolve, 200)
    script.onerror = reject
    document.head.appendChild(script)
  })
}

function waitForTurnstile() {
  return new Promise((resolve, reject) => {
    const maxAttempts = 50
    let attempts = 0

    const checkTurnstile = () => {
      if (window.turnstile?.render && typeof window.turnstile.render === 'function') resolve()
      else if (attempts < maxAttempts) {
        attempts++
        setTimeout(checkTurnstile, 100)
      } else {
        reject(new Error('Turnstile no se cargó'))
      }
    }

    checkTurnstile()
  })
}

async function renderWidget() {
  if (!container.value) {
    emit('error', new Error('Container no disponible'))
    return
  }

  try {
    widgetId = window.turnstile.render(container.value, {
      sitekey: effectiveSiteKey.value,
      theme: props.theme,
      size: props.invisible ? 'invisible' : 'normal',
      callback: token => emit('verified', token),
      'error-callback': err => emit('error', err),
      'expired-callback': () => {
        emit('expired')
        emit('error', new Error('Token expirado'))
      }
    })
  } catch (err) {
    emit('error', err)
    if (renderAttempts < 1) {
      renderAttempts++
      setTimeout(renderWidget, 500)
    }
  }
}

onMounted(async () => {
  try {
    await loadScript()
    await waitForTurnstile()
    await renderWidget()
  } catch (err) {
    emit('error', err)
  }
})

const executeWidget = () => {
  if (
    props.invisible &&
    widgetId !== null &&
    window.turnstile &&
    typeof window.turnstile.execute === 'function'
  ) {
    window.turnstile.execute(widgetId)
  }
}

const resetWidget = () => {
  emit('expired')

  if (widgetId !== null && window.turnstile) {
    window.turnstile.reset(widgetId)
    executeWidget()
  }
}

onBeforeUnmount(() => {
  if (window.turnstile && widgetId !== null) {
    try {
      window.turnstile.remove(widgetId)
    } catch (err) {
      emit('error', err)
    }
  }
})

defineExpose({
  reset: resetWidget,
  execute: executeWidget,
})
</script>
